import { useMemo, useState } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { forecastWeeks, totalLeaveWeeks } from "../lib/leave";
import { fmtMonthShort } from "../lib/format";
import InfoTip from "../components/InfoTip";
import { toast } from "sonner";
import DragScrollContainer from "../components/DragScrollContainer";
import RemovePersonButton from "../components/RemovePersonButton";

// Annual leave — active people × 52 weeks. Click a cell to cycle
// none → half week → full week → none. Feeds the Capacity matrix:
// leave reduces a person's available capacity that month.

const NEXT_VALUE = { 0: 0.5, 0.5: 1, 1: 0 };

export default function Leave() {
  const { rawData, setLeave, viewAs } = useData();
  const { user } = useAuth();
  // Only admin + leave_editor can write. Principals get a read-only view
  // (server enforces the same on PUT /api/leave/{person_id}).
  const canEdit = user?.role === "admin" || user?.role === "leave_editor";
  const weeks = useMemo(() => forecastWeeks(rawData), [rawData]);
  const people = rawData?.people || [];
  const leave = rawData?.leave || {};
  const [saving, setSaving] = useState(null); // `${personId}:${week}`

  const cycle = async (personId, week) => {
    if (!canEdit) return;
    const current = leave[personId]?.[week] || 0;
    const next = NEXT_VALUE[current] ?? 0;
    const key = `${personId}:${week}`;
    setSaving(key);
    try {
      await setLeave(personId, week, next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save leave");
    } finally {
      setSaving(null);
    }
  };

  // Month header groups: contiguous runs of weeks in the same month.
  const monthGroups = useMemo(() => {
    const groups = [];
    for (const w of weeks) {
      if (w.monthStart || groups.length === 0) groups.push({ month: w.month, span: 0 });
      groups[groups.length - 1].span += 1;
    }
    return groups;
  }, [weeks]);

  return (
    <div className="space-y-8" data-testid="leave-view">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="eyebrow">Planning</div>
          <h2 className="section-h mt-1 inline-flex items-center">
            Annual leave · {people.length} people × 52 weeks
            <InfoTip side="right" label="How leave works">
              Click a cell to cycle <strong>none → half week → full week</strong>.
              Leave feeds the Capacity matrix — it <strong>adds to</strong> a
              person's load in the affected month (a full week of leave =
              +25% on top of whatever's booked), so overload warnings
              account for who's actually here.
            </InfoTip>
          </h2>
        </div>
        <div className="flex items-center gap-4 text-xs font-mono-num">
          <LegendCell value={0} label="Working" />
          <LegendCell value={0.5} label="Half week" />
          <LegendCell value={1} label="Full week" />
        </div>
      </div>

      {!canEdit && (
        <div className="p-3 border border-[var(--rule-strong)] bg-[var(--paper-soft)] text-xs font-mono-num text-[var(--muted-fg)]" data-testid="leave-readonly-hint">
          Read-only view for principals — leave is edited by admin or the leave editor.
        </div>
      )}

      {viewAs && (
        <div className="p-3 border border-[#B45309] bg-[#FDF6EF] text-xs font-mono-num text-[#7C3A06]" data-testid="leave-viewas-hint">
          Leave is a firm-wide action — editing is unavailable while acting as a principal.
        </div>
      )}

      <DragScrollContainer
        className="border border-[var(--rule)] bg-white overflow-auto thin-scroll max-h-[calc(100vh-230px)]"
        data-testid="leave-grid"
      >
        <table className="text-xs border-collapse" style={{ minWidth: "100%" }}>
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 bg-white text-left px-4 py-2 eyebrow border-b border-r border-[var(--rule)] min-w-[160px]" rowSpan={2}>
                Person
              </th>
              {monthGroups.map((g, i) => (
                <th key={`${g.month}-${i}`} colSpan={g.span}
                    className="bg-white px-1 py-1.5 eyebrow text-left border-b border-l border-[var(--rule)]">
                  {fmtMonthShort(g.month)}
                </th>
              ))}
              <th className="bg-[var(--paper-soft)] px-3 py-1.5 eyebrow text-right border-b border-l border-[var(--rule-strong)] min-w-[70px]" rowSpan={2}>
                Total
              </th>
            </tr>
            <tr>
              {weeks.map((w) => (
                <th key={w.key}
                    className={`bg-white px-0.5 pb-1.5 font-mono-num text-[9px] text-[var(--soft-fg)] text-center border-b border-[var(--rule-strong)] min-w-[26px] ${w.monthStart ? "border-l border-[var(--rule)]" : ""}`}
                    title={`Week starting ${w.key}`}>
                  {w.day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {people.map((person) => {
              const byWeek = leave[person.id] || {};
              const total = totalLeaveWeeks(byWeek);
              return (
                <tr key={person.id} className="border-t border-[var(--rule)]/60" data-testid={`leave-row-${person.id}`}>
                  <td className="sticky left-0 z-10 bg-white px-4 py-1.5 text-[var(--ink)] border-r border-[var(--rule)] whitespace-nowrap">
                    <span className="inline-flex items-center justify-between gap-2 w-full">
                      {person.name}
                      <RemovePersonButton person={person} testidSuffix="-leave" />
                    </span>
                  </td>
                  {weeks.map((w) => {
                    const v = byWeek[w.key] || 0;
                    const busy = saving === `${person.id}:${w.key}`;
                    return (
                      <td key={w.key} className={`p-0.5 ${w.monthStart ? "border-l border-[var(--rule)]" : ""}`}>
                        <button
                          onClick={() => cycle(person.id, w.key)}
                          disabled={busy || !!viewAs || !canEdit}
                          className={`block w-full h-6 border transition-colors overflow-hidden relative ${cellCls(v)} ${busy ? "opacity-40" : ""} ${!canEdit ? "cursor-default" : ""} disabled:cursor-not-allowed`}
                          title={`${person.name} · wk ${w.label} · ${v === 1 ? "full week leave" : v === 0.5 ? "half week leave" : "working"}`}
                          data-testid={`leave-cell-${person.id}-${w.key}`}
                          aria-label={`${person.name} ${w.key}`}
                        >
                          {v === 0.5 && <span className="block w-1/2 h-full bg-[#B45309]" />}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5 font-mono-num text-right border-l border-[var(--rule-strong)] bg-[var(--paper-soft)]/40"
                      data-testid={`leave-total-${person.id}`}>
                    {total ? `${total} wk` : "—"}
                  </td>
                </tr>
              );
            })}
            {people.length === 0 && (
              <tr><td colSpan={54} className="px-4 py-10 text-center eyebrow text-[var(--soft-fg)]">No people in the dataset.</td></tr>
            )}
          </tbody>
        </table>
      </DragScrollContainer>

      <div className="text-xs font-mono-num text-[var(--soft-fg)]">
        Week columns show the Monday's date · leave rolls up per month and reduces available
        capacity on the <span className="text-[var(--ink)]">Capacity</span> view.
      </div>
    </div>
  );
}

const cellCls = (v) =>
  v === 1
    ? "bg-[var(--ink)] border-[var(--ink)]"
    : v === 0.5
      ? "bg-[#FDF0DA] border-[#B45309]"
      : "bg-white border-[var(--rule)] hover:border-[var(--rule-strong)] hover:bg-[var(--paper-soft)]";

function LegendCell({ value, label }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`inline-block w-5 h-4 border ${cellCls(value)} relative overflow-hidden`}>
        {value === 0.5 && <span className="block w-1/2 h-full bg-[#B45309]" />}
      </span>
      <span className="text-[var(--muted-fg)]">{label}</span>
    </span>
  );
}
